import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import './StudentLoanRemovalPage.css';
import {
  TestimonialCarousel,
  VideoTestimonials,
  DisputeEverywhere,
  Positions250K,
  TailoredPlanForm,
  AgencyMarquee,
} from '../components/ServiceDetailExtras';

const CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/Formalizeai/qA1Y2';

const SLR_AGENCIES = [
  'Nelnet', 'Navient', 'MOHELA', 'Aidvantage', 'Sallie Mae',
  'EdFinancial', 'Great Lakes', 'FedLoan Servicing',
  'Discover Student Loans', 'SoFi', 'Earnest', 'CommonBond',
  'ECSI', 'Firstmark Services',
  'Equifax', 'Experian', 'TransUnion',
  'LexisNexis', 'SageStream', 'ARS',
  'CFPB', 'FTC', 'State AG',
];

const SLR_PHOTO_TESTIMONIALS = [
  {
    src: '/testimonials/mohela-forgiven.jpg',
    quote: 'Your student loans have been forgiven — MOHELA, PSLF/TEPSLF. Thank you for your public service.',
    name: 'MOHELA Client',
    result: 'Federal loans forgiven',
  },
  {
    src: '/testimonials/slr/slr-03.jpg',
    quote: 'Borrower Defense Loan Discharge — Completed. $12,734 gone.',
    name: 'Borrower Defense Win',
    result: '$12,734 discharged',
  },
  {
    src: '/testimonials/slr/slr-02.jpg',
    quote: "Congratulations! You've paid off all your loans. Total Balance $0.",
    name: 'Nelnet Payoff',
    result: '$0 total balance',
  },
  {
    src: '/testimonials/slr/slr-01.jpg',
    quote: "You helped clear over $12,000 debt in student loans. Thanks again — you're the best.",
    name: '@thereallamarqus',
    result: '$12K+ cleared',
  },
];

const SLR_VIDEO_TESTIMONIALS = [
  {
    src: '/testimonials/slr/slr-client-win.mp4',
    quote: '"Watch this client walk through the receipts after their student loans were cleared."',
    name: 'Verified Client',
    result: 'Loans cleared on camera',
  },
  {
    src: '/testimonials/mina-voice.mp4',
    quote: "\"Y'all cleared my file in weeks — I'm still in shock. Thank you.\"",
    name: 'Mina',
    result: 'File cleared in weeks',
  },
];

const INCLUDED = [
  'Complete student loan removal from everywhere',
  'Covers federal, private, defaulted, and collections',
  'Nelnet, Navient, MOHELA, Aidvantage, Sallie Mae & more',
  'Reporting error identification',
  'Statute of limitations review',
  'Strategic dispute campaign until removed',
  'Balance & ownership challenges',
  'GUARANTEED results — no removal, no fee',
];

const PAIN_POINTS = [
  'Mortgage applications denied for debt-to-income ratio',
  'Business funding capped because of visible loan balances',
  'Auto loan rates jacked up by 3-5% over market',
  'Credit scores stuck under 700 for years',
  'Servicer records inaccurate, out of date, or outright wrong',
];

const StudentLoanRemovalPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToCheckout = (e) => {
    e.preventDefault();
    const el = document.getElementById('student-loan-checkout');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="slr-page" data-testid="student-loan-removal-page">
      {/* HERO */}
      <section className="slr-hero" data-testid="slr-hero">
        <div className="slr-hero-grid">
          <div className="slr-hero-body">
            <span className="slr-eyebrow">
              <Sparkles size={14} /> Student Loan Removal · Guaranteed
            </span>
            <h1 className="slr-title">
              Delete your <span className="accent">student loans</span> from everywhere —{' '}
              <span className="gold">or you don't pay.</span>
            </h1>
            <p className="slr-sub">
              Federal, private, defaulted, in collections — if a student loan is on your file,
              we remove it from everywhere it reports. No generic letters, no empty promises.
              If we can't remove them, you don't pay. Period.
            </p>

            <div className="slr-hero-stats">
              <div className="slr-stat">
                <div className="slr-stat-num">50-100+</div>
                <div className="slr-stat-lbl">Avg score lift post-removal</div>
              </div>
              <div className="slr-stat">
                <div className="slr-stat-num">All</div>
                <div className="slr-stat-lbl">Servicers covered</div>
              </div>
              <div className="slr-stat">
                <div className="slr-stat-num">100%</div>
                <div className="slr-stat-lbl">Pay-on-results guarantee</div>
              </div>
            </div>

            <div className="slr-hero-ctas">
              <a
                href={CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="slr-btn slr-btn-primary slr-btn-lg"
                data-testid="slr-cta-start"
                onClick={scrollToCheckout}
              >
                Start My Removal <ArrowRight size={18} />
              </a>
              <Link to="/services" className="slr-btn slr-btn-ghost">
                See All Services
              </Link>
            </div>

            <p className="slr-micro">
              <ShieldCheck size={14} /> Guaranteed removal · No results, no fee · Secured checkout
            </p>
          </div>
        </div>
      </section>

      {/* CLIENT PHOTO TESTIMONIALS — right under hero */}
      <TestimonialCarousel
        items={SLR_PHOTO_TESTIMONIALS}
        eyebrow="Real Student Loan Wins"
        title={<>Discharge letters. Payoff screens. <span className="sde-gold">$0 balances.</span></>}
        subtitle="These are the actual screenshots and messages real clients sent us after their student loans came off — PSLF forgiveness, Borrower Defense discharges, Nelnet $0 payoff screens, and the thank-you texts that followed."
      />

      {/* SERVICER COVERAGE */}
      <section className="slr-servicers" data-testid="slr-servicers" aria-label="Servicer coverage">
        <div className="slr-servicers-wrap">
          <span className="slr-servicers-eyebrow">We Remove Loans From</span>
          <div className="slr-servicers-row">
            <img src="/logos/servicers/nelnet.svg" alt="Nelnet" loading="lazy" />
            <img src="/logos/servicers/navient.svg" alt="Navient" loading="lazy" />
            <img src="/logos/servicers/mohela.svg" alt="MOHELA" loading="lazy" />
            <img src="/logos/servicers/aidvantage.svg" alt="Aidvantage" loading="lazy" />
            <img src="/logos/servicers/sallie-mae.svg" alt="Sallie Mae" loading="lazy" />
          </div>
          <p className="slr-servicers-plus">
            + Great Lakes · EdFinancial · FedLoan · Discover Student Loans · SoFi · Earnest · CommonBond · and every other servicer reporting your loans.
          </p>
        </div>
      </section>

      {/* PAIN */}
      <section className="slr-pain" data-testid="slr-pain">
        <div className="slr-pain-wrap">
          <span className="slr-eyebrow slr-eyebrow-red">
            <AlertTriangle size={14} /> The Real Cost
          </span>
          <h2 className="slr-section-title">What student loan reporting is actually costing you.</h2>
          <ul className="slr-pain-list">
            {PAIN_POINTS.map((p, i) => (
              <li key={i} data-testid={`slr-pain-${i}`}>
                <span className="slr-pain-dot" aria-hidden="true" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <p className="slr-pain-note">
            Removing them is not about dodging debt — it's about forcing accuracy, legal compliance,
            and giving yourself a shot at the mortgage, business funding, or auto approval you
            actually deserve.
          </p>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="slr-included" data-testid="slr-included">
        <div className="slr-included-grid">
          <div className="slr-included-copy">
            <span className="slr-eyebrow">What's Included</span>
            <h2 className="slr-section-title">
              The full <span className="accent">done-for-you</span> removal stack.
            </h2>
            <p className="slr-section-sub">
              One flat engagement. Every account on your file. Every bureau. Every dispute cycle
              until we win.
            </p>
          </div>
          <ul className="slr-included-list">
            {INCLUDED.map((item, i) => (
              <li key={i} data-testid={`slr-included-${i}`}>
                <CheckCircle2 size={18} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MID-PAGE BUY CTA with rotating agency banner */}
      <section className="slr-midbuy" data-testid="slr-midbuy">
        <div className="slr-midbuy-card">
          <div className="slr-midbuy-top">
            <div className="slr-midbuy-copy">
              <span className="slr-eyebrow slr-eyebrow-gold">
                <Sparkles size={13} /> Ready To Move
              </span>
              <h2 className="slr-midbuy-title">
                Seen enough? Start the removal <span className="gold">right now.</span>
              </h2>
              <p className="slr-midbuy-sub">
                No removal, no fee. CROA/FCRA compliant. Secured checkout — intake begins within 24 business hours.
              </p>
            </div>
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="slr-btn slr-btn-primary slr-btn-xl"
              data-testid="slr-midbuy-cta"
            >
              Start Student Loan Removal <ArrowRight size={20} />
            </a>
          </div>
          <AgencyMarquee agencies={SLR_AGENCIES} label="Every Servicer · Bureau · Agency We Pressure" />
        </div>
      </section>

      {/* VIDEO TESTIMONIALS — spaced away from photo testimonials with content between */}
      <VideoTestimonials
        items={SLR_VIDEO_TESTIMONIALS}
        eyebrow="Clients In Their Own Words"
        title={<>Hear the <span className="sde-gold">loan-free moment</span> on camera.</>}
        subtitle="Real clients, filmed on their own phones, after their student loans were removed from their reports and payoffs confirmed. No scripts, no actors."
      />

      {/* GUARANTEE */}
      <section className="slr-guarantee" data-testid="slr-guarantee">
        <div className="slr-guarantee-card">
          <div className="slr-guarantee-badge">
            <ShieldCheck size={34} />
          </div>
          <h2 className="slr-guarantee-title">
            No Removal. No Fee. <span className="gold">Period.</span>
          </h2>
          <p className="slr-guarantee-body">
            If we cannot remove your student loan tradelines from your credit reports, you don't pay
            a cent. We only win when you win — this is the standard we hold ourselves to and the
            standard our clients deserve.
          </p>
        </div>
      </section>

      {/* DISPUTE EVERYWHERE */}
      <DisputeEverywhere />

      {/* $250K FUNDING POSITIONING */}
      <Positions250K />

      {/* TAILORED PLAN FORM */}
      <TailoredPlanForm
        service="student-loan-removal"
        serviceLabel="Student Loan Removal"
      />

      {/* FINAL CHECKOUT */}
      <section className="slr-checkout" id="student-loan-checkout" data-testid="slr-checkout">
        <div className="slr-checkout-card">
          <span className="slr-eyebrow slr-eyebrow-gold">Final Step</span>
          <h2 className="slr-checkout-title">
            Start your removal today.
          </h2>
          <p className="slr-checkout-sub">
            Secure checkout via Fanbasis. You'll receive your intake and dispute kickoff within 24
            business hours.
          </p>
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="slr-btn slr-btn-primary slr-btn-xl"
            data-testid="slr-checkout-cta"
          >
            Begin Removal — Secure Checkout <ArrowRight size={20} />
          </a>
          <p className="slr-checkout-note">
            <ShieldCheck size={14} /> 100% pay-on-results guarantee · Encrypted checkout
          </p>
        </div>
      </section>
    </div>
  );
};

export default StudentLoanRemovalPage;
